import { useFigureCompositionStore } from '@/store/figure-composition';
import { FIGURE_TEMPLATE_PRESETS } from '@/lib/figureTemplates';
import { cn } from '@/lib/utils';

// Visual layout preview component
// Matches the actual canvas grid system: 12 cols x 12 rows (max height)
// This ensures the preview exactly matches what users will see when they apply the template
function LayoutPreview({ panels, cols = 12 }: { panels: Array<{ x: number; y: number; w: number; h: number }>; cols?: number }) {
  // Use the same grid system as the actual canvas: 12 columns, max 12 rows
  const gridCols = 12;
  const gridRows = 12; // Maximum height used in templates
  
  if (panels.length === 0) {
    return (
      <div className="w-full h-11 border border-dashed border-border/50 rounded-sm flex items-center justify-center bg-muted/20">
        <span className="text-[8px] text-muted-foreground/70 font-medium">Custom</span>
      </div>
    );
  }

  return (
    <div className="w-full h-11 relative bg-muted/40 rounded-sm overflow-hidden">
      {panels.map((panel, idx) => {
        // Calculate positions as percentages matching react-grid-layout's coordinate system
        // x and w are in column units (0-12), y and h are in row units (0-12)
        const leftPercent = (panel.x / gridCols) * 100;
        const topPercent = (panel.y / gridRows) * 100;
        const widthPercent = (panel.w / gridCols) * 100;
        const heightPercent = (panel.h / gridRows) * 100;
        
        return (
          <div
            key={idx}
            className="absolute bg-background border border-border/50"
            style={{
              left: `${leftPercent}%`,
              top: `${topPercent}%`,
              width: `${widthPercent}%`,
              height: `${heightPercent}%`,
            }}
          />
        );
      })}
    </div>
  );
}

export function TemplatePresetSelector() {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const applyTemplatePreset = useFigureCompositionStore((state) => state.applyTemplatePreset);

  if (!currentComposition) {
    return null;
  }

  return (
    <div className="mb-4">
      <h3 className="text-xs font-semibold mb-2 text-foreground/80">Template Presets</h3>
      <div className="grid grid-cols-3 gap-1.5">
        {FIGURE_TEMPLATE_PRESETS.map((preset) => {
          const isActive = currentComposition.templatePresetId === preset.id;
          return (
            <button
              key={preset.id}
              className={cn(
                'group relative p-2 rounded-md border transition-all cursor-pointer',
                'hover:border-primary/40',
                isActive 
                  ? 'border-primary/70 bg-primary/5' 
                  : 'border-border/50 bg-card'
              )}
              onClick={() => applyTemplatePreset(preset.id)}
            >
              {isActive && (
                <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-primary rounded-full z-10" />
              )}
              <LayoutPreview panels={preset.initialPanels} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

